import { Component, effect, HostListener, inject, viewChild, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { EpicrisisGridItemObject } from '@it2go/types';
import { MenuItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { Listbox, ListboxChangeEvent } from '@openng/optimus-ui/listbox';
import {
    InputFulltextComponent,
    IOption,
} from '../../../../shared/components/inputs/input-fulltext/input-fulltext.component';
import { FolderEpicrisisPageStore } from './folder-epicrisis-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { fb, goBack, isAllowed } from '../../../../shared/shared.global-service';
import { BaseServicePrintDialogComponent } from '../../../../../components/medical-service/print/base-service-print-dialog/base-service-print-dialog.component';
import { isInvalid } from '../../../../shared/helpers/validate';
import { checkOk, contentGlobalSuccess } from '../../../content.error';
import { folderEpicrisisFrag } from './folder-epicrisis-page.query';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { generateFilename } from '../../../../../utils/files.utils';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { formAddText } from '../../../../../utils/form.utils';
import { ServicePatternWysiwygComponent } from '../../../components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { FormsModule } from '@angular/forms';
import { UserChipComponent } from '../../../../../components/kit/user-chip/user-chip.component';
import { wysiwygPatterGroupsMedicalFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';
import { FolderEpicrisisPageHistoryState } from './folder-epicrisis-page.types';

@Component({
    selector: 'app-folder-epicrisis-page',
    templateUrl: './folder-epicrisis-page.component.html',
    styleUrls: ['./folder-epicrisis-page.component.scss'],
    providers: [FolderEpicrisisPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputFulltextComponent,
        ButtonComponent,
        ServicePatternWysiwygComponent,
        Listbox,
        TranslatePipe,
        FormsModule,
        PrimeTemplate,
        UserChipComponent,
        ContextMenu,
    ],
})
export class FolderEpicrisisPageComponent extends SubscriptionComponent {
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(FolderEpicrisisPageStore);
    protected readonly dialogService = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );
    private readonly goBackAfterSave = Boolean(
        (history.state as FolderEpicrisisPageHistoryState | null)?.goBackAfterSave,
    );

    protected selectedEpicrisis: EpicrisisGridItemObject | null = null;

    public readonly wysiwyg = viewChild<ServicePatternWysiwygComponent>(
        ServicePatternWysiwygComponent,
    );

    protected form = fb.group({
        search: fb.control<string>(''),
        selectedEpicrisisData: fb.control<string>('', { nonNullable: true }),
    });

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: 'update',
            icon: 'pi pi-file-edit',
            command: () => {
                this.update();
            },
        },
        {
            label: 'delete',
            icon: 'pi pi-times',
            command: () => {
                this.delete();
            },
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    protected contextItem: number = 0;

    constructor() {
        super();

        effect(() => {
            if (
                !isAllowed['folderRecord.create']() ||
                this.folderStore.isTerminated()
            ) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        });

        this.store.onSuccess(
            () => this.fetchData(),
            'createEpicrisis',
            'updateEpicrisis',
            'deleteEpicrisis',
        );

        effect(() => {
            const epicrises = this.store.epicrises();

            if (epicrises.length > 0) {
                this.selectedEpicrisis = epicrises[0];
                if (this.selectedEpicrisis) {
                    this.form.patchValue({
                        selectedEpicrisisData: this.selectedEpicrisis['data'],
                    });
                } else {
                    this.form.reset();
                }
            }
        });

        effect(() => {
            if (this.store.folder() && this.store.epicrisisSharingIds()) {
                this.fetchData();
            }
        });

        this.contextMenuItemsAll.forEach((it) => {
            it.label = this.translate.instant(
                `database.persons.epicrisis.${it.label}`,
            );
        });
    }

    public listChange(index: ListboxChangeEvent): void {
        if (index.value) {
            this.selectedEpicrisis = this.store.epicrises()[index.value.index];
            this.form.patchValue({
                selectedEpicrisisData:
                    this.store.epicrises()[index.value.index]['data'],
            });
        }
    }

    protected async save() {
        if (isInvalid(this.form)) return;

        if (!this.store.folder()) return;
        const res = await this.store.api.createEpicrisis({
            folderId: this.store.folder()?.id || 0,
            createdAt: new Date(),
            data: this.form.value.selectedEpicrisisData || '',
        });

        this.wysiwyg()?.resetBackupValues();

        if (checkOk(res) && this.goBackAfterSave) goBack();
    }

    protected selectedEpicrisisChange(
        selectedEpicrisisSearch: IOption | null,
    ): void {
        this.selectedEpicrisis =
            selectedEpicrisisSearch as any as EpicrisisGridItemObject;
        this.form.patchValue({
            selectedEpicrisisData: selectedEpicrisisSearch?.data ?? '',
        });
    }

    protected async delete() {
        if (this.contextItem) {
            await this.store.api.deleteEpicrisis({ id: this.contextItem });
            contentGlobalSuccess.next('common.deleted');
        }
    }

    protected async update() {
        if (this.contextItem) {
            const res = await this.store.api.updateEpicrisis({
                id: this.contextItem,
                data: this.form.value.selectedEpicrisisData || '',
            });

            this.wysiwyg()?.resetBackupValues();

            if (checkOk(res, 'update') && this.goBackAfterSave) goBack();
        }
    }

    protected print() {
        this.dialogService.open(BaseServicePrintDialogComponent, {
            header: this.translate.instant('common.print'),
            data: {
                serviceId: this.store.serviceId(),
                onStart: (value: any) =>
                    this.backgroundTaskLauncher.start({
                        type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                        payload: {
                            folderId: this.store.folderId() || 0,
                            kind: ClientDocumentExportKind.EPICRISIS,
                            input: {
                                filename: generateFilename(
                                    'folder.epicrisis.label',
                                ),
                                id: this.selectedEpicrisis?.id || 0,
                                headerId: value.headerId || 0,
                                footerId: value.footerId || 0,
                                archive: value.archive || false,
                            },
                        },
                        alreadyRunningMessage:
                            'person.documentExport.backgroundTask.messages.alreadyRunning',
                        failedMessage:
                            'person.documentExport.backgroundTask.messages.startFailed',
                    }),
            },
        });
    }

    @HostListener('document:contextmenu')
    public rightClickAny() {
        this.ctx.hide();
    }

    @HostListener('document:click')
    public leftClickAny() {
        this.ctx.hide();
    }

    public rightClick(
        menu: ContextMenu,
        event: MouseEvent,
        item: EpicrisisGridItemObject,
    ) {
        this.contextItem = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(
            this.selectedEpicrisis?.id === item.id ? 0 : 1,
        );
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    private fetchData() {
        if (!this.store.folderId() || !this.store.epicrisisSharingIds()) return;
        this.store.api
            .getEpicrises(
                FilterBuilder.singlePage({
                    folderId: [
                        this.store.folderId(),
                        ...this.store.epicrisisSharingIds()!,
                    ],
                }),
                folderEpicrisisFrag,
            )
            .then();
    }

    protected readonly isAllowed = isAllowed;
    protected readonly ServicePatternStructureUsageEnum =
        ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly wysiwygPatterGroupsMedicalFolder =
        wysiwygPatterGroupsMedicalFolder;
}
